import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import useHttpClient from './useHttpClient';

export interface IEmailContact {
  name: string;
  address: string;
}

export interface IAttachment {
  name: string,
  content: string,
  isBase64: boolean,
  contentType?: string,
  folderId?: number,
}

export interface IEmailData {
  subject: string;
  from: IEmailContact,
  to: IEmailContact[];
  cc?: IEmailContact[];
  html: string;
  headers: {
    date: string;
  },
  attachments: IAttachment[];
  emailId: { partner: string | null }
}

interface IEmailResponse {
  noteId: {
    native: number;
  };
}

interface IEmailPayload {
  addEmail: (payload: IEmailData) => Promise<IEmailResponse>;
  loading: boolean;
  error?: Error;
  called: boolean;
  noteId?: number;
}

export class EmailPayload {
  subject: string = '';

  from: IEmailContact = {
    name: '',
    address: '',
  };

  to: IEmailContact[] = [{
    name: '',
    address: '',
  }];

  cc?: IEmailContact[] = [{
    name: '',
    address: '',
  }];

  html: string = '';

  headers: { date: string; } = { date: '' };

  attachments: IAttachment[] = [];

  emailId: { partner: string | null } = { partner: null };
}

const useMutateEmail = (projectId?: number): IEmailPayload => {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { mutateAsync, error, isPending, isSuccess, isError, data } = useMutation({
    mutationFn: async (emailData: IEmailData) => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      // Convert emailData to a JSON string
      const emailString = JSON.stringify(emailData);

      // Encode the string into Base64 using a UTF-8 conversion
      const utf8ToBase64 = (str: string) => {
        return btoa(unescape(encodeURIComponent(str)));
      };

      const encodedEmailData = utf8ToBase64(emailString);

      const response = await httpClient.post(
        `/projects/${projectId}/encodedEmails`,
        {
          base64Encoding: encodedEmailData,
        },
        {
          timeout: 600000, // 10 minutes for large emails with attachments
        }
      );
      return response.data as IEmailResponse;
    },
    onSuccess: () => {
      // Invalidate ALL note queries - this should trigger useQueryNote to refetch
      queryClient.invalidateQueries({ queryKey: ['note'] });
      queryClient.invalidateQueries({ queryKey: ['noteComments'] });
      // Invalidate project-related queries if needed
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      }
    },
  });

  const addEmail = useCallback(async (emailData: IEmailData) => {
    return mutateAsync(emailData);
  }, [mutateAsync]);

  return {
    addEmail,
    error: error as Error,
    loading: isPending,
    called: isSuccess || isError,
    noteId: data?.noteId?.native,
  };
};

export default useMutateEmail;